import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Line } from '@react-three/drei';
import { useTheme } from '@/theme/ThemeContext';
import { useMemo, useRef, useState, useEffect } from 'react';
import * as THREE from 'three';

// Laser Beam Component
const LaserBeam = ({ scale, color }: { scale: number, color: string }) => {
    const group = useRef<THREE.Group>(null);
    const [showLaser, setShowLaser] = useState(false);
    const [intensity, setIntensity] = useState(0);

    useEffect(() => {
        setShowLaser(scale >= 0.85);
        setIntensity(Math.min(1, (scale - 0.85) * 6.67));
    }, [scale]);

    useFrame((state) => {
        if (!group.current || !showLaser) return;
        group.current.rotation.z = state.clock.getElapsedTime() * 0.05;
    });

    // Ray configs
    const numRays = 32;
    const minLength = 8;
    const maxLength = 15;
    const rayConfigs = useMemo(() => {
        const rays = [];
        for (let i = 0; i < numRays; i++) {
            const angle = (i / numRays) * Math.PI * 2;
            const length = minLength + Math.random() * (maxLength - minLength);
            rays.push({ angle, length });
        }
        return rays;
    }, []);

    if (!showLaser) return null;

    return (
        <group ref={group} position={[0, 0, -1]}>
            {/* Smooth Cylinder Rays */}
            {rayConfigs.map((ray, i) => {
                const x = Math.cos(ray.angle) * ray.length / 2;
                const y = Math.sin(ray.angle) * ray.length / 2;
                // Use THREE.Euler for rotation
                const rotation = new THREE.Euler(0, 0, ray.angle - Math.PI / 2);
                return (
                    <mesh
                        key={`ray-${i}`}
                        position={[x, y, 0]}
                        rotation={rotation}
                    >
                        <cylinderGeometry args={[0.05, 0.05, ray.length, 32]} />
                        <meshBasicMaterial
                            color={color}
                            transparent
                            opacity={0.85 * intensity}
                        />
                    </mesh>
                );
            })}

            {/* Center Glow */}
            <mesh position={[0, 0, 0]}>
                <circleGeometry args={[4, 128]} />
                <meshBasicMaterial
                    color={color}
                    transparent
                    opacity={0.3 * intensity}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Outer Glow Rings */}
            {[0.8, 0.9, 1.0].map((scale, index) => (
                <mesh key={`glow-${index}`} position={[0, 0, 0]}>
                    <circleGeometry args={[12 * scale, 256]} />
                    <meshBasicMaterial
                        color={color}
                        transparent
                        opacity={0.1 * intensity}
                        side={THREE.DoubleSide}
                    />
                </mesh>
            ))}
        </group>
    );
};

const Background3D = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const primaryColor = isDark ? '#f97316' : '#0f766e';

    // Get card scale from About component
    const [cardScale, setCardScale] = useState(0.7);
    useEffect(() => {
        const handleScroll = () => {
            const card = document.querySelector('#about .scale-animation');
            if (!card) return;
            const rect = card.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const visible = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
            const progress = Math.min(1, Math.max(0, visible / rect.height));
            setCardScale(0.7 + 0.3 * progress);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="fixed inset-0 -z-10">
            <Canvas
                camera={{ position: [0, 0, 25], fov: 45 }}
                dpr={[1, 2]}
                gl={{
                    antialias: true,
                    alpha: true,
                    powerPreference: "high-performance",
                }}
            >
                <color attach="background" args={[isDark ? '#000000' : '#ffffff']} />

                {/* Ambient Light */}
                <ambientLight intensity={isDark ? 0.4 : 0.6} />

                {/* Laser Beam Effect */}
                <LaserBeam scale={cardScale} color={primaryColor} />

                {/* Controls */}
                <OrbitControls
                    enableZoom={false}
                    enablePan={false}
                    enableRotate={false}
                    autoRotate
                    autoRotateSpeed={0.5}
                />
            </Canvas>
        </div>
    );
};

export default Background3D; 